import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, TrendingUp, Users, Calendar, FileText, Download, MapPin, Clock, CheckCircle, MoreHorizontal, Search, Filter, Check, RefreshCw, Trash2 } from 'lucide-react';
import SummaryCards from '@/components/SummaryCards';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHeader, TableRow, TableHead } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Loader2 } from 'lucide-react';
import { saveAs } from 'file-saver';
import Loader from '@/components/Loader';

interface DashboardStats {
  totalMeetings: number;
  totalAttendance: number;
  totalExcused: number;
  activeLinks: number;
  eligibleCount: number;
  totalMembers: number;
}

interface CorpsMember {
  id: string;
  full_name: string;
  email: string;
  state_code: string;
  attendance_count: number;
  excused_count: number;
  first_meeting_status: 'present' | 'absent' | 'excused';
  second_meeting_status: 'present' | 'absent' | 'excused';
  final_meeting_status: 'present' | 'absent' | 'excused';
  eligible: boolean;
  last_attendance?: string;
}

interface Meeting {
  id: string;
  _id: string;
  title: string;
  datetime: string;
  meeting_type: string;
  clearance_stage: 'first' | 'second' | 'final';
  location: {
    latitude: number;
    longitude: number;
    radius: number;
  };
  attendance_count: number;
  link_token: string;
}

interface Attendance {
  _id: string;
  meeting_id: string;
  admin_id: string;
  full_name: string;
  state_code: string;
  timestamp: string;
  member_latitude: number;
  member_longitude: number;
  meeting_title: string;
  meeting_type: string;
  clearance_stage: 'first' | 'second' | 'final';
  meeting_datetime: string;
}

interface AttendanceSubmission {
  _id: string;
  meeting_id: string;
  admin_id: string;
  full_name: string;
  state_code: string;
  timestamp: string;
  member_latitude: number;
  member_longitude: number;
  meeting_title: string;
  meeting_type: string;
  clearance_stage: 'first' | 'second' | 'final';
  meeting_datetime: string;
}

interface MemberAttendanceStatus {
  state_code: string;
  full_name: string;
  first_meeting: boolean;
  second_meeting: boolean;
  final_meeting: boolean;
  is_cleared: boolean;
  last_attendance: string;
}

// Add custom gold color theme
const goldTheme = {
  primary: {
    DEFAULT: '#D4AF37',
    foreground: '#000000',
    hover: '#BFA030',
    active: '#A68B28',
  },
  secondary: {
    DEFAULT: '#F5E6C3',
    foreground: '#000000',
    hover: '#E6D7B3',
    active: '#D4C5A3',
  }
};

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalMeetings: 0,
    totalAttendance: 0,
    totalExcused: 0,
    activeLinks: 0,
    eligibleCount: 0,
    totalMembers: 0
  });
  const [members, setMembers] = useState<CorpsMember[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showEligibleOnly, setShowEligibleOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [excuseDialog, setExcuseDialog] = useState({
    isOpen: false,
    memberId: '',
    meetingType: '',
    memberName: ''
  });
  const [excuseReason, setExcuseReason] = useState('');
  const [newCorpsMember, setNewCorpsMember] = useState({
    name: '',
    stateCode: ''
  });
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [latestMeeting, setLatestMeeting] = useState<Meeting | null>(null);
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [attendanceSubmissions, setAttendanceSubmissions] = useState<AttendanceSubmission[]>([]);
  const [memberAttendanceStatus, setMemberAttendanceStatus] = useState<MemberAttendanceStatus[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<CorpsMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Create a truly unified member list from both members and attendance status
  const stateCodeSet = new Set([
    ...members.map(m => m.state_code),
    ...memberAttendanceStatus.map(s => s.state_code)
  ]);

  const unifiedMembers = Array.from(stateCodeSet).map(state_code => {
    const member = members.find(m => m.state_code === state_code);
    const attendanceStatus = memberAttendanceStatus.find(s => s.state_code === state_code);

    return {
      id: member?.id || state_code,
      full_name: member?.full_name || attendanceStatus?.full_name || '',
      email: member?.email || '',
      state_code,
      attendance_count: member?.attendance_count ?? 0,
      excused_count: member?.excused_count ?? 0,
      first_meeting_status: member?.first_meeting_status || (attendanceStatus?.first_meeting ? 'present' : 'absent'),
      second_meeting_status: member?.second_meeting_status || (attendanceStatus?.second_meeting ? 'present' : 'absent'),
      final_meeting_status: member?.final_meeting_status || (attendanceStatus?.final_meeting ? 'present' : 'absent'),
      eligible: member?.eligible ?? attendanceStatus?.is_cleared ?? false,
      last_attendance: member?.last_attendance || attendanceStatus?.last_attendance || '',
      // Unified/derived fields
      first_meeting_completed: attendanceStatus?.first_meeting || (member?.first_meeting_status === 'present' || member?.first_meeting_status === 'excused'),
      second_meeting_completed: attendanceStatus?.second_meeting || (member?.second_meeting_status === 'present' || member?.second_meeting_status === 'excused'),
      final_meeting_completed: attendanceStatus?.final_meeting || (member?.final_meeting_status === 'present' || member?.final_meeting_status === 'excused'),
      is_cleared: attendanceStatus?.is_cleared ?? member?.eligible ?? false,
      last_attendance_submission: attendanceStatus?.last_attendance || member?.last_attendance || ''
    };
  });

  // Filter unified members based on search query and active tab
  const filteredUnifiedMembers = (() => {
    try {
      return unifiedMembers.filter(member => {
        // Safely handle null/undefined values
        const fullName = member.full_name || '';
        const stateCode = member.state_code || '';
        const searchTerm = searchQuery || '';
        
        const matchesSearch = 
          fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stateCode.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (activeTab === 'eligible') {
          return matchesSearch && member.is_cleared;
        } else if (activeTab === 'pending') {
          return matchesSearch && !member.is_cleared;
        }
        
        return matchesSearch;
      });
    } catch (error) {
      console.error('Error filtering members:', error);
      // Return empty array if filtering fails
      return [];
    }
  })();

  // Sort unified members by last attendance
  const sortedUnifiedMembers = (() => {
    try {
      return [...filteredUnifiedMembers].sort((a, b) => {
        if (!a.last_attendance_submission) return 1;
        if (!b.last_attendance_submission) return -1;
        return new Date(b.last_attendance_submission).getTime() - new Date(a.last_attendance_submission).getTime();
      });
    } catch (error) {
      console.error('Error sorting members:', error);
      // Return unsorted array if sorting fails
      return filteredUnifiedMembers;
    }
  })();

  // Add error boundary component
  const ErrorDisplay = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="flex flex-col items-center justify-center p-8 space-y-4">
      <div className="text-red-600 text-center">
        <p className="font-medium">Something went wrong</p>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
      <Button 
        onClick={onRetry}
        variant="outline"
        className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        Retry
      </Button>
    </div>
  );

  // Modify fetchDashboardData to be more resilient
  const fetchDashboardData = async () => {
    if (!user?.group_id) {
      console.error('No group_id available');
      setError('User group not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data in parallel with individual error handling
      const fetchWithTimeout = async (url: string, timeout = 10000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        try {
          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        } catch (e) {
          clearTimeout(timeoutId);
          throw e;
        }
      };

      // Fetch data with individual error handling
      const [statsData, membersData, meetingsData, attendancesData] = await Promise.allSettled([
        fetchWithTimeout(`${import.meta.env.VITE_API_URL}/api/admin/stats?group_id=${user.group_id}`),
        fetchWithTimeout(`${import.meta.env.VITE_API_URL}/api/admin/members?group_id=${user.group_id}`),
        fetchWithTimeout(`${import.meta.env.VITE_API_URL}/api/admin/meetings?group_id=${user.group_id}`),
        fetchWithTimeout(`${import.meta.env.VITE_API_URL}/api/admin/${user.id}/attendances`)
      ]);

      // Handle individual fetch results
      const stats = statsData.status === 'fulfilled' ? statsData.value : null;
      const members = membersData.status === 'fulfilled' ? membersData.value : [];
      const meetings = meetingsData.status === 'fulfilled' ? meetingsData.value : [];
      const attendances = attendancesData.status === 'fulfilled' ? attendancesData.value : [];

      // Log any failed requests
      if (statsData.status === 'rejected') console.error('Failed to fetch stats:', statsData.reason);
      if (membersData.status === 'rejected') console.error('Failed to fetch members:', membersData.reason);
      if (meetingsData.status === 'rejected') console.error('Failed to fetch meetings:', meetingsData.reason);
      if (attendancesData.status === 'rejected') console.error('Failed to fetch attendances:', attendancesData.reason);

      // Process members with their attendance status
      const processedMembers = (members || []).map((member: any) => ({
        id: member._id || member.id,
          full_name: member.full_name,
          email: member.email || '',
          state_code: member.state_code,
          attendance_count: member.attendance_count || 0,
          excused_count: member.excused_count || 0,
          first_meeting_status: member.first_meeting_status || 'absent',
          second_meeting_status: member.second_meeting_status || 'absent',
          final_meeting_status: member.final_meeting_status || 'absent',
          eligible: member.eligible || false,
          last_attendance: member.last_attendance
        }));

      // Calculate stats with fallbacks
      const eligibleCount = processedMembers.filter((member: CorpsMember) => 
        (member.first_meeting_status === 'present' || member.first_meeting_status === 'excused') &&
        (member.second_meeting_status === 'present' || member.second_meeting_status === 'excused') &&
        (member.final_meeting_status === 'present' || member.final_meeting_status === 'excused')
      ).length;

      const totalExcused = processedMembers.reduce((sum: number, member: CorpsMember) => 
        sum + (member.excused_count || 0), 0
      );

      const totalAttendance = processedMembers.reduce((sum: number, member: CorpsMember) => 
        sum + (member.attendance_count || 0), 0
      );

      // Update stats with fallback values
      setStats({
        totalMeetings: meetings?.length || 0,
        totalAttendance: totalAttendance,
        totalExcused: totalExcused,
        activeLinks: (meetings || []).filter((meeting: Meeting) => 
          new Date(meeting.datetime) > new Date()
        ).length || 0,
        eligibleCount: eligibleCount,
        totalMembers: processedMembers.length
      });

      // Update other state with fallbacks
      setMembers(processedMembers);
      setMeetings(meetings || []);
      setAttendances(attendances || []);
      
      // Set first meeting as selected if none selected
      if (!selectedMeeting && meetings?.length > 0) {
        setSelectedMeeting(meetings[0]._id);
      }

      // Reset retry count on successful fetch
      setRetryCount(0);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
      
      // Implement exponential backoff for retries
      if (retryCount < 3) {
        const timeout = Math.min(1000 * Math.pow(2, retryCount), 10000);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchDashboardData();
        }, timeout);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMeetingAttendances = async (meetingId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/meetings/${meetingId}/attendances`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch attendances');
      }

      const data = await response.json();
      setAttendances(data);

      // Update member attendance status based on the latest attendance records
      if (latestMeeting) {
        const updatedMembers = members.map(member => {
          const attendance = data.find((a: Attendance) => a.state_code === member.state_code);
          if (attendance) {
            const meetingType = latestMeeting.meeting_type.toLowerCase();
            const statusField = `${meetingType}_meeting_status` as keyof CorpsMember;
            
            const updatedMember = {
              ...member,
              [statusField]: 'present'
            };
            
            // Recalculate eligibility
            updatedMember.eligible = (
              (updatedMember.first_meeting_status === 'present' || updatedMember.first_meeting_status === 'excused') &&
              (updatedMember.second_meeting_status === 'present' || updatedMember.second_meeting_status === 'excused') &&
              (updatedMember.final_meeting_status === 'present' || updatedMember.final_meeting_status === 'excused')
            );
            
            return updatedMember;
          }
          return member;
        });
        
        setMembers(updatedMembers);
      }
    } catch (error) {
      console.error('Error fetching attendances:', error);
      // Don't show error toast here as it might not be critical
    }
  };

  const fetchAdminAttendances = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${user.id}/attendances`);
      if (!response.ok) {
        throw new Error('Failed to fetch attendances');
      }
      const data = await response.json();
      setAttendances(data);
    } catch (error) {
      console.error('Error fetching attendances:', error);
      toast.error('Failed to load attendance records');
    }
  };

  const fetchLatestMeeting = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${user?.id}/latest-meeting`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch latest meeting');
      }

      const data = await response.json();
      setLatestMeeting(data);
      
      // Fetch attendances for the latest meeting
      if (data._id) {
        await fetchMeetingAttendances(data._id);
      }
    } catch (error) {
      console.error('Error fetching latest meeting:', error);
      // Don't show error toast here as it might not be critical
    }
  };

  const fetchAttendanceSubmissions = async () => {
    if (!user?.group_id || !user?.id) return null;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/${user.id}/attendance-submissions`, {
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch attendance submissions: ${response.status}`);
      }

      const data = await response.json();
      setAttendanceSubmissions(data);
      return data;
    } catch (error) {
      console.error('Error fetching attendance submissions:', error);
      // Don't show toast here as it might not be critical
      return null;
    }
  };

  const updateMemberAttendanceStatus = (submissions: AttendanceSubmission[]) => {
    const statusMap = new Map<string, MemberAttendanceStatus>();

    // Process all submissions
    submissions.forEach(submission => {
      if (!statusMap.has(submission.state_code)) {
        statusMap.set(submission.state_code, {
          state_code: submission.state_code,
          full_name: submission.full_name,
          first_meeting: false,
          second_meeting: false,
          final_meeting: false,
          is_cleared: false,
          last_attendance: submission.timestamp
        });
      }

      const status = statusMap.get(submission.state_code)!;
      
      // Update meeting status based on clearance stage
      switch (submission.clearance_stage) {
        case 'first':
          status.first_meeting = true;
          break;
        case 'second':
          status.second_meeting = true;
          break;
        case 'final':
          status.final_meeting = true;
          break;
      }

      // Update last attendance if this is more recent
      if (new Date(submission.timestamp) > new Date(status.last_attendance)) {
        status.last_attendance = submission.timestamp;
      }
    });

    // Check clearance status for all members
    statusMap.forEach(status => {
      status.is_cleared = status.first_meeting && status.second_meeting && status.final_meeting;
    });

    const statusArray = Array.from(statusMap.values());
    setMemberAttendanceStatus(statusArray);

    // Update stats to keep eligible count in sync
    const clearedCount = statusArray.filter(status => status.is_cleared).length;
    
    // Update both the stats and members state to maintain consistency
    setStats(prevStats => ({
      ...prevStats,
      eligibleCount: clearedCount
    }));

    // Update members state to reflect the same eligibility status
    setMembers(prevMembers => 
      prevMembers.map(member => {
        const status = statusArray.find(s => s.state_code === member.state_code);
        if (status) {
          return {
            ...member,
            eligible: status.is_cleared,
            first_meeting_status: status.first_meeting ? 'present' : 'absent',
            second_meeting_status: status.second_meeting ? 'present' : 'absent',
            final_meeting_status: status.final_meeting ? 'present' : 'absent'
          };
        }
        return member;
      })
    );
  };

  const downloadAttendanceSubmissions = () => {
    try {
      // Create CSV header
      let csvContent = "Name,State Code,First Meeting,Second Meeting,Final Meeting,Clearance Status,Last Attendance\n";
      
      // Add each member's status as a row
      memberAttendanceStatus.forEach(status => {
        const row = [
          status.full_name,
          status.state_code,
          status.first_meeting ? 'Completed' : 'Pending',
          status.second_meeting ? 'Completed' : 'Pending',
          status.final_meeting ? 'Completed' : 'Pending',
          status.is_cleared ? 'Cleared for Final Clearance' : 'Not Cleared',
          format(new Date(status.last_attendance), 'PPp')
        ];
        csvContent += row.join(',') + "\n";
      });
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `attendance-status-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Attendance status downloaded successfully');
    } catch (error) {
      console.error('Error downloading attendance status:', error);
      toast.error('Failed to download attendance status');
    }
  };

  const handleExcuseAbsence = async (meetingId: string, userId: string) => {
    try {
      const response = await fetch('/api/admin/excuse-absence', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          meetingId, 
          userId,
          reason: excuseReason,
          groupId: user?.group_id 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to excuse absence');
      }

      toast.success('Absence excused successfully');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error excusing absence:', error);
      toast.error('Failed to excuse absence');
    }
  };

  const handleExcuseClick = (memberId: string, meetingType: string) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setExcuseDialog({
        isOpen: true,
        memberId,
        meetingType,
        memberName: member.full_name
      });
    }
  };

  const submitExcuse = async () => {
    if (!excuseDialog.memberId || !excuseDialog.meetingType || !excuseReason.trim()) {
      toast.error('Please provide a reason for the excuse');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/admin/excuse-absence', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          memberId: excuseDialog.memberId,
          meetingType: excuseDialog.meetingType,
          reason: excuseReason,
          groupId: user?.group_id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit excuse');
      }

      toast.success(`${excuseDialog.memberName} has been excused from the ${excuseDialog.meetingType} meeting`);
      fetchDashboardData(); // Refresh data

      // Close dialog and reset state
      setExcuseDialog({
        isOpen: false,
        memberId: '',
        meetingType: '',
        memberName: ''
      });
      setExcuseReason('');
    } catch (error) {
      console.error('Error submitting excuse:', error);
      toast.error('Failed to submit excuse');
    } finally {
      setSubmitting(false);
    }
  };

  const addCorpsMember = async () => {
    if (!newCorpsMember.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: newCorpsMember.name,
          state_code: newCorpsMember.stateCode,
          group_id: user?.group_id
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add corps member');
      }

      const data = await response.json();
      
      // Create the member object with all required fields
      const updatedMember: CorpsMember = {
        id: data.id,
        full_name: data.full_name,
        email: data.email || '',
        state_code: data.state_code,
        attendance_count: data.attendance_count || 0,
        excused_count: data.excused_count || 0,
        first_meeting_status: data.first_meeting_status || 'absent',
        second_meeting_status: data.second_meeting_status || 'absent',
        final_meeting_status: data.final_meeting_status || 'absent',
        eligible: data.eligible || false,
        last_attendance: data.last_attendance
      };

      // Update the members list
      setMembers(prevMembers => {
        // Check if member already exists
        const existingMemberIndex = prevMembers.findIndex(m => m.id === updatedMember.id);
        
        if (existingMemberIndex !== -1) {
          // Update existing member
          const newMembers = [...prevMembers];
          newMembers[existingMemberIndex] = updatedMember;
          return newMembers;
        } else {
          // Add new member
          return [...prevMembers, updatedMember];
        }
      });

      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalMembers: prevStats.totalMembers + (data.isNewMember ? 1 : 0),
        eligibleCount: data.eligible ? prevStats.eligibleCount + 1 : prevStats.eligibleCount
      }));

      // Show appropriate success message
      if (data.first_meeting_status === 'present' && data.second_meeting_status === 'absent') {
        toast.success(`${data.full_name} marked present for First Meeting`);
      } else if (data.second_meeting_status === 'present' && data.final_meeting_status === 'absent') {
        toast.success(`${data.full_name} marked present for Second Meeting`);
      } else if (data.final_meeting_status === 'present') {
        toast.success(`${data.full_name} marked present for Final Meeting and is now eligible!`);
      }

      setNewCorpsMember({ name: '', stateCode: '' });
      setAddMemberDialogOpen(false);

      // Refresh the dashboard data to ensure everything is in sync
      await fetchDashboardData();
    } catch (error) {
      console.error('Error adding corps member:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add corps member');
    } finally {
      setSubmitting(false);
    }
  };

  // Update the downloadCSV function to export the unified data
  const downloadCSV = () => {
    try {
      const dataToExport = activeTab === 'eligible' 
        ? unifiedMembers.filter(m => m.is_cleared)
        : activeTab === 'pending'
        ? unifiedMembers.filter(m => !m.is_cleared)
        : unifiedMembers;
      
      let csvContent = "Name,State Code,Email,First Meeting Status,Second Meeting Status,Final Meeting Status,First Meeting Completed,Second Meeting Completed,Final Meeting Completed,Clearance Status,Attendance Count,Excused Count,Last Attendance,Last Attendance Submission\n";
      
      dataToExport.forEach(member => {
        const row = [
          member.full_name,
          member.state_code,
          member.email,
          member.first_meeting_status,
          member.second_meeting_status,
          member.final_meeting_status,
          member.first_meeting_completed ? 'Yes' : 'No',
          member.second_meeting_completed ? 'Yes' : 'No',
          member.final_meeting_completed ? 'Yes' : 'No',
          member.is_cleared ? 'Cleared' : 'Not Cleared',
          member.attendance_count,
          member.excused_count,
          member.last_attendance ? format(new Date(member.last_attendance), 'PPp') : 'Never',
          member.last_attendance_submission ? format(new Date(member.last_attendance_submission), 'PPp') : 'Never'
        ];
        csvContent += row.join(',') + "\n";
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `cds-comprehensive-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Comprehensive report downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  const handleCreateMeeting = async (meetingData: any) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...meetingData,
          group_id: user?.group_id,
          admin_id: user?.id
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create meeting');
      }

      toast.success('Meeting created successfully!');
      
      // Copy attendance URL to clipboard
      if (data.attendanceUrl) {
        await navigator.clipboard.writeText(data.attendanceUrl);
        toast.success('Attendance link copied to clipboard!');
      }

      // Refresh meetings list
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create meeting');
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchAdminAttendances();
    }
  }, [user?.id]);

  // Update the Badge variants to match shadcn's available variants
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'present':
        return 'default';
      case 'excused':
        return 'secondary';
      case 'absent':
        return 'destructive';
      default:
        return 'default';
    }
  };

  // Update the getMeetingStatusBadge function to use gold instead of blue
  const getMeetingStatusBadge = (date: string) => {
    const now = new Date();
    const meetingDate = new Date(date);
    
    // Check if the meeting is today
    const isToday = meetingDate.toDateString() === now.toDateString();
    
    if (isToday) {
      return (
        <Badge variant="outline" className="bg-[#F5E6C3] text-[#D4AF37] border-[#D4AF37]">
          Today
        </Badge>
      );
    }
    
    if (meetingDate > now) {
      return (
        <Badge variant="outline" className="bg-[#F5E6C3] text-[#D4AF37] border-[#D4AF37]">
          Upcoming
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-[#F5E6C3] text-[#D4AF37] border-[#D4AF37]">
        <Check className="mr-1 h-3 w-3" />
        Completed
      </Badge>
    );
  };

  const handleDeleteClick = (member: CorpsMember) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/members/${memberToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete member');
      }

      toast.success('Member deleted successfully');
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      fetchDashboardData(); // Refresh the list
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete member');
    }
  };

  // Add useEffect for initial load and retry handling
  useEffect(() => {
    if (user?.group_id) {
      // Fetch both dashboard data and attendance submissions
      const fetchAllData = async () => {
        try {
          await fetchDashboardData();
          const submissions = await fetchAttendanceSubmissions();
          if (submissions) {
            updateMemberAttendanceStatus(submissions);
          }
        } catch (error) {
          console.error('Error fetching all data:', error);
          // Don't set error state here as fetchDashboardData handles its own errors
        }
      };

      fetchAllData();
    }
  }, [user?.group_id, retryCount]);

  // Update refreshData to handle both fetches
  const refreshData = async () => {
    try {
      await fetchDashboardData();
      const submissions = await fetchAttendanceSubmissions();
      if (submissions) {
        updateMemberAttendanceStatus(submissions);
      }
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    }
  };

  // Update the loading state in the stats cards
  const StatsCards = () => (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
      {loading ? (
        Array(6).fill(0).map((_, i) => (
          <Card key={i} className="bg-white border-[#D4AF37] animate-pulse">
            <CardHeader className="p-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))
      ) : (
        <>
          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Total Meetings</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.totalMeetings}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Total Check-ins</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.totalAttendance}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Excused Members</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.totalExcused}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Active Links</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.activeLinks}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Eligible Members</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.eligibleCount}</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#D4AF37] hover:shadow-lg transition-all duration-200">
            <CardHeader className="p-4">
              <CardTitle className="text-sm font-medium text-[#D4AF37]">Total Members</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold">{stats.totalMembers}</div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );

  // Update the main render to handle errors
  if (error && !loading) {
    return (
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <ErrorDisplay 
          message={error} 
          onRetry={() => {
            setRetryCount(0);
            fetchDashboardData();
          }} 
        />
      </div>
    );
  }

  // Update the loading state
  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container mx-auto space-y-6 p-4 md:p-6 lg:p-8">
      {/* Header Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-1"
        >
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl bg-gradient-to-r from-[#D4AF37] to-[#F5E6C3] bg-clip-text text-transparent">
            Hi, {user?.full_name}
          </h1>
          <p className="text-sm text-muted-foreground">Welcome to your CDS Dashboard</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col gap-2 sm:flex-row"
        >
          <Button
            variant="outline"
            onClick={refreshData}
            className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setAddMemberDialogOpen(true)}
            className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
          >
            <Users className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Add Corps Member</span>
            <span className="sm:hidden">Add Member</span>
          </Button>
          
          <Button 
            asChild 
            className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#BFA030] text-white transition-colors duration-200"
          >
            <Link to="/admin/meetings/new">
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">New Meeting</span>
              <span className="sm:hidden">New</span>
            </Link>
          </Button>
        </motion.div>
      </div>
      
      {/* Stats Cards */}
      <StatsCards />
      
      {/* Corps Members Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <CardTitle>Corps Members & Attendance</CardTitle>
              <CardDescription className="hidden sm:inline">
                Track attendance and manage clearance eligibility - Unified Report
              </CardDescription>
            </div>
            
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              
              <Button 
                onClick={downloadCSV} 
                variant="outline" 
                className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
              >
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Export Report</span>
                <span className="sm:hidden">CSV</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all" className="flex items-center justify-center">
                <Users className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">All</span> ({unifiedMembers.length})
              </TabsTrigger>
              <TabsTrigger value="eligible" className="flex items-center justify-center">
                <CheckCircle className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Eligible</span> ({unifiedMembers.filter(m => m.is_cleared).length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center justify-center">
                <Clock className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Pending</span> ({unifiedMembers.filter(m => !m.is_cleared).length})
              </TabsTrigger>
            </TabsList>
            
            <div className="space-y-4">
              {loading ? (
                <div className="grid gap-3">
                  {Array(6).fill(0).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          <div className="flex gap-2">
                            <div className="h-5 bg-gray-200 rounded w-16"></div>
                            <div className="h-5 bg-gray-200 rounded w-16"></div>
                            <div className="h-5 bg-gray-200 rounded w-16"></div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : sortedUnifiedMembers.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Users className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No members found</h3>
                    <p className="text-gray-500 text-sm">Try adjusting your search or filters to find members.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {sortedUnifiedMembers.map((member) => (
                    <Card key={member.id} className="hover:shadow-md transition-all duration-200 border-l-4 border-l-[#D4AF37] group">
                      <CardContent className="p-4">
                        {/* Header with name and status */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900 truncate">{member.full_name}</h3>
                              <Badge 
                                variant={member.is_cleared ? 'default' : 'destructive'} 
                                className={`${member.is_cleared ? 'bg-[#D4AF37] hover:bg-[#BFA030]' : ''} text-xs`}
                              >
                                {member.is_cleared ? '✓ Eligible' : '✗ Not Eligible'}
                              </Badge>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <span className="font-medium">State:</span>
                                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{member.state_code}</code>
                              </span>
                              {member.email && (
                                <span className="hidden sm:flex items-center gap-1">
                                  <span className="font-medium">Email:</span>
                                  <span className="truncate text-xs">{member.email}</span>
                                </span>
                              )}
                            </div>
                            {member.email && (
                              <span className="flex items-center gap-1 sm:hidden mt-1">
                                <span className="font-medium text-xs">Email:</span>
                                <span className="truncate text-xs">{member.email}</span>
                              </span>
                            )}
                          </div>
                          
                          {/* Actions dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent 
                              align="end" 
                              className="w-[160px] border-[#D4AF37] bg-white shadow-lg"
                            >
                              <DropdownMenuItem 
                                className="hover:bg-[#F5E6C3] focus:bg-[#F5E6C3] cursor-pointer transition-all duration-200"
                                onClick={() => handleExcuseClick(member.id, 'First')}
                              >
                                <Clock className="mr-2 h-4 w-4" />
                                Excuse First
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="hover:bg-[#F5E6C3] focus:bg-[#F5E6C3] cursor-pointer transition-all duration-200"
                                onClick={() => handleExcuseClick(member.id, 'Second')}
                              >
                                <Clock className="mr-2 h-4 w-4" />
                                Excuse Second
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="hover:bg-[#F5E6C3] focus:bg-[#F5E6C3] cursor-pointer transition-all duration-200"
                                onClick={() => handleExcuseClick(member.id, 'Final')}
                              >
                                <Clock className="mr-2 h-4 w-4" />
                                Excuse Final
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600 hover:bg-red-50 focus:bg-red-50 cursor-pointer transition-all duration-200"
                                onClick={() => handleDeleteClick(member)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {/* Meeting Status Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <span className="text-xs font-medium text-gray-700">First</span>
                            <Badge variant={getBadgeVariant(member.first_meeting_status)} className="text-xs px-1.5 py-0.5">
                              {member.first_meeting_completed ? '✓' : 
                               member.first_meeting_status === 'excused' ? '⚠' : '✕'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <span className="text-xs font-medium text-gray-700">Second</span>
                            <Badge variant={getBadgeVariant(member.second_meeting_status)} className="text-xs px-1.5 py-0.5">
                              {member.second_meeting_completed ? '✓' : 
                               member.second_meeting_status === 'excused' ? '⚠' : '✕'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <span className="text-xs font-medium text-gray-700">Final</span>
                            <Badge variant={getBadgeVariant(member.final_meeting_status)} className="text-xs px-1.5 py-0.5">
                              {member.final_meeting_completed ? '✓' : 
                               member.final_meeting_status === 'excused' ? '⚠' : '✕'}
                            </Badge>
                          </div>
                        </div>

                        {/* Stats and Last Attendance */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-gray-100">
                          <div className="flex gap-3 text-xs">
                            <span className="flex items-center gap-1 text-gray-600">
                              <Users className="h-3 w-3" />
                              <span className="font-medium">Att:</span>
                              <span className="font-semibold">{member.attendance_count}</span>
                            </span>
                            <span className="flex items-center gap-1 text-gray-600">
                              <Clock className="h-3 w-3" />
                              <span className="font-medium">Exc:</span>
                              <span className="font-semibold">{member.excused_count}</span>
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span className="font-medium">Last:</span>
                            {member.last_attendance_submission ? (
                              <span className="font-medium">
                                {format(new Date(member.last_attendance_submission), 'MMM d')}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Never</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Excuse Dialog */}
      <Dialog 
        open={excuseDialog.isOpen} 
        onOpenChange={(open) => {
          setExcuseDialog(prev => ({ ...prev, isOpen: open }));
          if (!open) setExcuseReason('');
        }}
      >
        <DialogContent className="sm:max-w-md border-[#D4AF37]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">Excuse Absence</DialogTitle>
            <DialogDescription>
              Excuse {excuseDialog.memberName} from {excuseDialog.meetingType} meeting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="form-group">
              <Label htmlFor="excuse-reason">Reason for Excuse</Label>
              <Textarea
                id="excuse-reason"
                placeholder="Provide a reason for excusing this absence"
                value={excuseReason}
                onChange={(e) => setExcuseReason(e.target.value)}
                className="min-h-[100px]"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setExcuseDialog(prev => ({ ...prev, isOpen: false }));
                setExcuseReason('');
              }}
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={submitExcuse}
              disabled={!excuseReason.trim() || submitting}
              className="bg-[#D4AF37] hover:bg-[#BFA030] text-white transition-colors duration-200"
            >
              {submitting ? 'Submitting...' : 'Submit Excuse'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Corps Member Dialog */}
      <Dialog
        open={addMemberDialogOpen}
        onOpenChange={setAddMemberDialogOpen}
      >
        <DialogContent className="sm:max-w-md border-[#D4AF37]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">Add Corps Member</DialogTitle>
            <DialogDescription>
              Add a new corps member to your CDS group
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="form-group">
              <Label htmlFor="member-name">Full Name</Label>
              <Input
                id="member-name"
                placeholder="John Doe"
                value={newCorpsMember.name}
                onChange={(e) => setNewCorpsMember(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="state-code">State Code (Optional)</Label>
              <Input
                id="state-code"
                placeholder="AB/23A/1234"
                value={newCorpsMember.stateCode}
                onChange={(e) => setNewCorpsMember(prev => ({ ...prev, stateCode: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setAddMemberDialogOpen(false)}
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-200"
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={addCorpsMember}
              disabled={!newCorpsMember.name.trim() || submitting}
              className="bg-[#D4AF37] hover:bg-[#BFA030] text-white transition-all duration-200"
            >
              {submitting ? 'Adding...' : 'Add Corps Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Member Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md border-[#D4AF37]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">Delete Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {memberToDelete?.full_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-colors duration-200"
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={handleDeleteMember}
              className="bg-red-600 hover:bg-red-700 text-white transition-colors duration-200"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add global styles using className */}
      <div className="[&_.transition-all]:transition-all [&_.transition-all]:duration-200 [&_.hover-scale]:transform [&_.hover-scale]:scale-100 [&_.hover-scale:hover]:scale-105 [&_.focus-ring]:outline-none [&_.focus-ring]:ring-2 [&_.focus-ring]:ring-[#D4AF37] [&_.active-scale]:transform [&_.active-scale]:scale-95" />
    </div>
  );
};

export default AdminDashboard;
